import React, { useState, useEffect } from 'react';
import '../../styles/HighlightTester.css';

// Test data extracted from syntaxlens-debug-prompt-1756478978434.md
const TEST_CODE = `fn create_user() -> Box<User> {
    let user = Box::new(User {
        name: String::from("Alice"),
        age: 30,
    });
    user  // No "return" needed
}`;

const TEST_SELECTIONS = [
  {
    id: 'function_signature',
    text: 'fn create_user() -> Box<User>',
    reason: 'Function signature with ownership transfer',
    novelty: 0.9,
    diffType: 'paradigm',
    expectedLine: 1,
    expectedStart: 0,
    expectedEnd: 31
  },
  {
    id: 'box_allocation',
    text: 'Box::new(User {',
    reason: 'Safe heap allocation pattern',
    novelty: 0.8,
    diffType: 'semantic',
    expectedLine: 2,
    expectedStart: 16,
    expectedEnd: 31
  },
  {
    id: 'string_creation',
    text: 'String::from("Alice")',
    reason: 'Owned string creation',
    novelty: 0.7,
    diffType: 'semantic',
    expectedLine: 3,
    expectedStart: 15,
    expectedEnd: 36
  },
  {
    id: 'type_annotation',
    text: 'name: String',
    reason: 'Type system with ownership',
    novelty: 0.8,
    diffType: 'paradigm',
    expectedLine: 3,
    expectedStart: 9,
    expectedEnd: 21
  },
  {
    id: 'field_init',
    text: 'age: 30,',
    reason: 'Struct field initialization',
    novelty: 0.6,
    diffType: 'syntax',
    expectedLine: 4,
    expectedStart: 9,
    expectedEnd: 17
  },
  {
    id: 'implicit_return',
    text: 'user',
    reason: 'Implicit return expression',
    novelty: 0.7,
    diffType: 'idiom',
    expectedLine: 6,
    expectedStart: 4,
    expectedEnd: 8
  },
  {
    id: 'return_comment',
    text: '// No "return" needed',
    reason: 'Documentation of implicit return',
    novelty: 0.6,
    diffType: 'idiom',
    expectedLine: 6,
    expectedStart: 10,
    expectedEnd: 31
  }
];

// Position finding function (copied from ZenPlaygroundOptimized)
const findTextPosition = (sourceCode: string, searchText: string) => {
  if (!sourceCode || !searchText) return null;
  
  const normalizedSource = sourceCode.replace(/\r\n/g, '\n');
  const normalizedSearch = searchText.replace(/\r\n/g, '\n').trim();
  
  const normalizeWhitespace = (text: string) => {
    return text
      .replace(/\s+/g, ' ')
      .replace(/\n\s*/g, '\n')
      .trim();
  };
  
  let startIndex = normalizedSource.indexOf(normalizedSearch);
  
  if (startIndex === -1) {
    // Try fuzzy matching
    const normalizedSourceForSearch = normalizeWhitespace(normalizedSource);
    const normalizedSearchForSearch = normalizeWhitespace(normalizedSearch);
    
    const fuzzyIndex = normalizedSourceForSearch.indexOf(normalizedSearchForSearch);
    if (fuzzyIndex !== -1) {
      const firstWords = normalizedSearch.split(/\s+/).slice(0, 3).join(' ').trim();
      startIndex = normalizedSource.indexOf(firstWords);
      
      if (startIndex === -1) {
        const sourceLines = normalizedSource.split('\n');
        for (let i = 0; i < sourceLines.length; i++) {
          const line = sourceLines[i];
          if (normalizeWhitespace(line).includes(normalizeWhitespace(firstWords))) {
            const lineStart = sourceLines.slice(0, i).join('\n').length + (i > 0 ? 1 : 0);
            const originalMatchPos = line.indexOf(firstWords.split(' ')[0]);
            if (originalMatchPos !== -1) {
              startIndex = lineStart + originalMatchPos;
              break;
            }
          }
        }
      }
    }
  }
  
  if (startIndex === -1) return null;
  
  const beforeStart = normalizedSource.substring(0, startIndex);
  const startLine = beforeStart.split('\n').length - 1;
  const startColumn = beforeStart.split('\n').pop()?.length || 0;
  
  const endIndex = startIndex + normalizedSearch.length;
  const beforeEnd = normalizedSource.substring(0, endIndex);
  const endLine = beforeEnd.split('\n').length - 1;
  const endColumn = beforeEnd.split('\n').pop()?.length || 0;
  
  return {
    startPoint: { row: startLine, column: startColumn },
    endPoint: { row: endLine, column: endColumn }
  };
};

// Highlighting calculation function (copied from ZenPlaygroundOptimized)
const calculateLineHighlighting = (lineNumber: number, line: string, activeAnchor: any) => {
  try {
    if (!activeAnchor || !activeAnchor.span || !activeAnchor.span.startPoint) {
      return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
    }

    const startRow = activeAnchor.span.startPoint.row + 1;
    const endRow = activeAnchor.span.endPoint?.row ? activeAnchor.span.endPoint.row + 1 : startRow;
    
    if (typeof startRow !== 'number' || typeof endRow !== 'number' || startRow < 1 || endRow < 1) {
      return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
    }
    
    if (lineNumber < startRow || lineNumber > endRow) {
      return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
    }

    let highlightStartCol = 0;
    let highlightEndCol = line.length;

    if (lineNumber === startRow && typeof activeAnchor.span.startPoint.column === 'number') {
      highlightStartCol = Math.max(0, Math.min(activeAnchor.span.startPoint.column, line.length));
    }

    if (lineNumber === endRow && activeAnchor.span.endPoint && typeof activeAnchor.span.endPoint.column === 'number') {
      highlightEndCol = Math.max(0, Math.min(activeAnchor.span.endPoint.column, line.length));
    }

    highlightStartCol = Math.max(0, Math.min(highlightStartCol, line.length));
    highlightEndCol = Math.max(highlightStartCol, Math.min(highlightEndCol, line.length));

    if (highlightStartCol === highlightEndCol) {
      if (line.length === 0) {
        highlightEndCol = 0;
      } else {
        highlightEndCol = Math.min(highlightStartCol + 1, line.length);
      }
    }

    return { 
      isPartOfActiveSelection: true, 
      highlightStartCol, 
      highlightEndCol 
    };
    
  } catch (error) {
    console.error('Error in calculateLineHighlighting:', error);
    return { isPartOfActiveSelection: false, highlightStartCol: 0, highlightEndCol: line.length };
  }
};

interface TestResult {
  id: string;
  text: string;
  found: boolean;
  position?: any;
  expectedLine: number;
  actualLine?: number;
  accuracy: 'perfect' | 'good' | 'poor' | 'failed';
  method: 'exact' | 'fuzzy' | 'failed';
}

export const HighlightTester: React.FC = () => {
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [debugMode, setDebugMode] = useState(false);

  // Run all tests when component mounts
  useEffect(() => {
    runAllTests();
  }, []);

  const runAllTests = () => {
    const results = TEST_SELECTIONS.map(selection => {
      const position = findTextPosition(TEST_CODE, selection.text);
      
      let accuracy: TestResult['accuracy'] = 'failed';
      let method: TestResult['method'] = 'failed';
      
      if (position) {
        const actualLine = position.startPoint.row + 1;
        method = TEST_CODE.indexOf(selection.text) !== -1 ? 'exact' : 'fuzzy';
        
        if (actualLine === selection.expectedLine) {
          accuracy = 'perfect';
        } else if (Math.abs(actualLine - selection.expectedLine) <= 1) {
          accuracy = 'good';
        } else {
          accuracy = 'poor';
        }
      }

      return {
        id: selection.id,
        text: selection.text,
        found: !!position,
        position,
        expectedLine: selection.expectedLine,
        actualLine: position ? position.startPoint.row + 1 : undefined,
        accuracy,
        method
      };
    });

    setTestResults(results);
  };

  const getAccuracyColor = (accuracy: TestResult['accuracy']) => {
    switch (accuracy) {
      case 'perfect': return '#48bb78';
      case 'good': return '#ed8936';
      case 'poor': return '#f56565';
      case 'failed': return '#e53e3e';
      default: return '#a0aec0';
    }
  };

  const renderCodeWithHighlighting = () => {
    if (!selectedTest) return TEST_CODE;

    const selection = TEST_SELECTIONS.find(s => s.id === selectedTest);
    const result = testResults.find(r => r.id === selectedTest);
    
    if (!selection || !result?.position) {
      return TEST_CODE;
    }

    // Create anchor object for highlighting
    const anchor = {
      tempId: selection.id,
      text: selection.text,
      span: result.position
    };

    return TEST_CODE.split('\n').map((line, index) => {
      const lineNumber = index + 1;
      const { isPartOfActiveSelection, highlightStartCol, highlightEndCol } = 
        calculateLineHighlighting(lineNumber, line, anchor);

      return (
        <div key={lineNumber} className="test-code-line">
          <span className="test-line-number">{lineNumber}</span>
          <span className="test-code-content">
            {isPartOfActiveSelection ? (
              <>
                <span>{line.substring(0, highlightStartCol)}</span>
                <span className="test-highlight active">
                  {line.substring(highlightStartCol, highlightEndCol)}
                </span>
                <span>{line.substring(highlightEndCol)}</span>
              </>
            ) : (
              <span>{line}</span>
            )}
          </span>
        </div>
      );
    });
  };

  return (
    <div className="highlight-tester">
      <div className="test-header">
        <h1>🎯 Highlighting Test Suite</h1>
        <div className="test-controls">
          <button onClick={runAllTests} className="test-button">
            🔄 Re-run Tests
          </button>
          <label className="debug-toggle">
            <input 
              type="checkbox" 
              checked={debugMode} 
              onChange={(e) => setDebugMode(e.target.checked)} 
            />
            Debug Mode
          </label>
        </div>
      </div>

      <div className="test-layout">
        {/* Test Results Panel */}
        <div className="test-results">
          <h2>Test Results ({testResults.filter(r => r.found).length}/{testResults.length})</h2>
          <div className="test-list">
            {testResults.map(result => (
              <div 
                key={result.id}
                className={`test-item ${selectedTest === result.id ? 'selected' : ''}`}
                onClick={() => setSelectedTest(result.id)}
                style={{ borderLeft: `4px solid ${getAccuracyColor(result.accuracy)}` }}
              >
                <div className="test-name">{result.id.replace('_', ' ')}</div>
                <div className="test-status">
                  <span className={`status-badge ${result.accuracy}`}>
                    {result.accuracy.toUpperCase()}
                  </span>
                  <span className="method-badge">{result.method}</span>
                </div>
                <div className="test-details">
                  Expected: Line {result.expectedLine} | 
                  Actual: {result.actualLine ? `Line ${result.actualLine}` : 'Not found'}
                </div>
                <div className="test-text">"{result.text.substring(0, 40)}..."</div>
              </div>
            ))}
          </div>
        </div>

        {/* Code Display Panel */}
        <div className="test-code-panel">
          <h2>Code with Highlighting</h2>
          {selectedTest && (
            <div className="test-info">
              Testing: <strong>{selectedTest.replace('_', ' ')}</strong>
              <br />
              Text: "{TEST_SELECTIONS.find(s => s.id === selectedTest)?.text}"
            </div>
          )}
          <div className="test-code-container">
            <pre className="test-code">
              {renderCodeWithHighlighting()}
            </pre>
          </div>
        </div>
      </div>

      {/* Debug Information */}
      {debugMode && selectedTest && (
        <div className="debug-panel">
          <h3>🐛 Debug Information</h3>
          <div className="debug-content">
            {(() => {
              const selection = TEST_SELECTIONS.find(s => s.id === selectedTest);
              const result = testResults.find(r => r.id === selectedTest);
              return (
                <pre>
                  {JSON.stringify({
                    selection: {
                      text: selection?.text,
                      expectedLine: selection?.expectedLine,
                      reason: selection?.reason
                    },
                    result: {
                      found: result?.found,
                      position: result?.position,
                      actualLine: result?.actualLine,
                      accuracy: result?.accuracy,
                      method: result?.method
                    },
                    codeAnalysis: {
                      exactMatch: TEST_CODE.includes(selection?.text || ''),
                      normalizedMatch: TEST_CODE.replace(/\s+/g, ' ').includes(
                        (selection?.text || '').replace(/\s+/g, ' ')
                      )
                    }
                  }, null, 2)}
                </pre>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default HighlightTester;